"use client";
import { CloseCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { DatePicker, Space } from "antd";
import "antd/dist/reset.css";
import type { Dayjs } from "dayjs";
import type { CellRenderInfo } from "@rc-component/picker/lib/interface";
import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import CurrencySymbol from "@/Components/Common/CurrencySymbol/CurrencySymbol";
import { _eventBus } from "../../../core/events/EventBus";
import { evtTree } from "../../../Orchestrator/ConstantTree";
import { useRecState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { SearchStateType } from "../../../Orchestrator/Types/Index";
import "./Calendar.css";
import { isEmpty } from "../../../Lib/Utils/validationUtil";
import dayjs from "dayjs";
import { TrainTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import enLocale from "antd/es/date-picker/locale/en_GB";
import arLocale from "antd/es/date-picker/locale/ar_EG";

const { RangePicker } = DatePicker;

const CalendarComponent: React.FC<any> = ({
  value,
  onChange,
  format = "YYYY-MM-DD",
  placeholder = ["From Date", "To Date"],
  width = "100%",
  priceData = {},
  showTime = false,
  disabledDates,
  className = "",
  size = "middle",
  openByDefault = false,
  returnHintTextId = "addAReturn",
}) => {
  const intl = useIntl();  
  const [isCalendarOpen, setIsCalendarOpen] = useState(openByDefault);
  const [priceMap, setPriceMap] = useState<{ [key: string]: number | string }>(
    {}
  );
  const [isInboundFocused, setIsInboundFocused] = useState(false);
  const [isInboundActive, setIsInboundActive] = useState(false);
  const [isEndDateActive, setIsEndDateActive] = useState(false);
  const [search] = useRecState<SearchStateType>("searchStateAtom");
  //   const [bookedTrips] = useRecState<BookedTrips[]>("bookedTripsAtom");
  const [calendarRangeBestPrice] = useRecState<any[]>(
    "calendarRangeBestPriceAtom"
  );
  const [calendarDayInterval] = useRecState<number>("calendarDayIntervalAtom");

  useEffect(() => {
    if (isCalendarOpen) {
      if (!isEmpty(search.from) && !isEmpty(search.to)) {
        const type = isEndDateActive
          ? TrainTypeEnum.inbound
          : TrainTypeEnum.outbound;
        setTimeout(() => {
          _eventBus.emit({
            eventName: evtTree.SearchBar.SelectDate.BestPrice,
            data: {
              from: search.from,
              to: search.to,
              calendarDayInterval: calendarDayInterval,
              type: type,
            },
          });
        }, 1);
      }
    }
  }, [
    isCalendarOpen,
    search.from,
    search.to,
    isEndDateActive,
    calendarDayInterval,
  ]);

  useEffect(() => {
    if (!isEmpty(search.from) && !isEmpty(search.to)) {
      const newPriceMap = (calendarRangeBestPrice || []).reduce(
        (acc: { [key: string]: number }, item) => {
          const dateKey = dayjs(item.date).format("YYYY-MM-DD");
          acc[dateKey] = item.price;
          return acc;
        },
        {}
      );
      setPriceMap(newPriceMap);
    } else {
      const newPriceMap: { [key: string]: string } = {};
      if (calendarDayInterval) {
        for (let i = 0; i <= calendarDayInterval; i++) {
          const date = dayjs().add(i, "day").format("YYYY-MM-DD");
          newPriceMap[date] = "--";
        }
      }
      setPriceMap(newPriceMap);
    }
  }, [calendarRangeBestPrice, search.from, search.to, calendarDayInterval]);

  const onCalendarChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates === null) {
      const newDates: [Dayjs | null, Dayjs | null] = [value[0], null];
      if (onChange) onChange(newDates);
    } else {
      if (onChange) onChange(dates);
      // Close calendar if both dates are selected
      if (dates[0] && dates[1]) {
        setIsCalendarOpen(false);
      }
    }
  };

  const renderClearIcon = () => (
    <CloseCircleOutlined
      onClick={(e) => {
        e.stopPropagation();
        onCalendarChange(null);
      }}
    />
  );

  const cellRender = useCallback(
    (current: string | number | Dayjs, info: CellRenderInfo<Dayjs>) => {
      if (
        info.type !== "date" ||
        typeof current !== "object" ||
        !("format" in current)
      ) {
        return info.originNode;
      }

      const dateString = current.format("YYYY-MM-DD");
      const price = priceMap[dateString];

      return (
        <div className="ant-picker-cell-inner">
          <div className="show_date">{current.date()}</div>
          {price != null ? (
            <span className="show_price">
              {typeof price === "number" ? (
                <><CurrencySymbol /> {price}</>
              ) : (
                price
              )}
            </span>
          ) : <span className="show_price">--</span>}
        </div>
      );
    },
    [priceMap]
  );
  useEffect(() => {
    setIsCalendarOpen(openByDefault);
  }, [openByDefault]);

  const handleInputFocus = (e: React.FocusEvent<HTMLElement>) => {
    // const dateRange = e.target.getAttribute("date-range");
    // if (dateRange) {
    //   setIsInboundFocused(dateRange === "end" ? true : false);
    //   setIsInboundActive(dateRange === "end" ? true : false);
    // }
    const dateRange = e.target.getAttribute("date-range");
    if (dateRange) {
      setIsEndDateActive(dateRange === "end");
    }
  };

  useEffect(() => {
    if (isCalendarOpen == false) {
      _eventBus.emit({
        eventName: evtTree.SearchBar.SelectOrigin.Blur,
      });
    }
  }, [isCalendarOpen]);


  useEffect(() => {
    const returnInput = document.querySelector('.ant-picker-range-separator + .ant-picker-input');
    if (returnInput && value?.length > 0 && value[0] && !value[1]) {
      returnInput.setAttribute('data-return-text', intl.formatMessage({ id: returnHintTextId }));
    } else if (returnInput) {
      returnInput.removeAttribute('data-return-text');
    }
  }, [value, intl, returnHintTextId]);

  // Enhanced disabledDate logic
  const enhancedDisabledDate = (current: Dayjs) => {
    let baseDisabled = disabledDates ? disabledDates(current) : false;
    if (baseDisabled) return true;

    // When start date input is focused (isEndDateActive is false) and end date is selected, disable dates after end date
    if (!isEndDateActive && value && value[1]) {
      if (current.isAfter(value[1], "day")) {
        return true;
      }
    }
    // When end date input is focused (isEndDateActive is true) and start date is selected, disable dates before start date
    if (isEndDateActive && value && value[0]) {
      if (current.isBefore(value[0], "day")) {
        return true;
      }
    }

    return baseDisabled;
  };

  return (
    <div className="range-picker-container">
      <label
        className={`range-picker-label filled ${value[1] && "inbound-date"}`}
      >
        {" "}
        {/* Comment this as now we are showing Outbound date as today date by default <label className={`range-picker-label ${isFilled ? "filled" : ""}`}> */}
        <span>{placeholder[0]}</span>
        <span>{placeholder[1]}</span>
      </label>
      <Space orientation="vertical" size={12} className={className}>
        <RangePicker
          locale={intl.locale === "en" ? enLocale : arLocale}
          value={value}
          onChange={onCalendarChange}
          format={format}
          style={{ width }}
          placeholder={placeholder}
          cellRender={cellRender}
          showTime={showTime}
          disabledDate={enhancedDisabledDate}
          // disabledDate={disabledDates}
          size={size}
          allowClear={
            search.inboundDate ? { clearIcon: renderClearIcon() } : false
          }
          open={isCalendarOpen}
          onOpenChange={(open) => setIsCalendarOpen(open)}
          onFocus={handleInputFocus}
          onBlur={() => setIsInboundFocused(false)}
          onCalendarChange={onCalendarChange}
          allowEmpty={[false, true]}
          renderExtraFooter={() => (
            <>
              {
                <div className="calendar-footer">
                  <InfoCircleOutlined style={{ fontSize: "20px" }} />
                  <span>
                    {intl.formatMessage({
                      id:
                        search.from && search.to
                          ? "pricesForOneAdult"
                          : "selectOriginDestination",
                    })}
                  </span>
                </div>
              }
            </>
          )}
          classNames={{ popup: { root: `change-date-booking home-page-date-picker  ${intl.locale === "en" ? "" : "rtl-range-picker"}
            ${"custom-range-picker-dropdown"}

            ${isInboundActive && "inbound-focused"}
          ` } }}
          onClick={() => {
            _eventBus.emit({
              eventName: evtTree.SearchBar.SelectDate.Open,
            });
          }}
        />
        {/* ${bookedTrips?.length > 0 && 'have-last-booking'} */}
      </Space>
      {/* {value?.length > 0 && value[0] && !value[1] && !isInboundFocused && (
        <label className={`range-picker-sub-label`}>
          <span>{intl.formatMessage({ id: "addAReturn" })}</span>
        </label>
      )} */}
    </div>
  );
};

export default CalendarComponent;
